<template>
    <div
        ref="accordion"
        :items="items"
        class="base-accordion"
        :scroll-into-view="false"
        :autoclose="false"
    >
        <div
            v-for="(item, index) in items"
            :key="index"
            class="base-accordion__item"
        >
            <button
                class="base-accordion__button"
                @click="toggle(index)"
            >
                {{ item.title }}
                <BaseIcon
                    class="base-accordion__icon"
                    :icon="openIndexes.includes(index) ? 'minus' : 'plus'"
                />
            </button>

            <transition
                name="expand"
                @enter="enter"
                @after-enter="afterEnter"
                @leave="leave"
            >
                <!-- eslint-disable vue/no-v-html  -->
                <div
                    v-show="openIndexes.includes(index)"
                    class="base-accordion__content"
                >
                    <div
                        class="base-accordion__content-html"
                        v-html="item.content?.html || item.content"
                    />
                </div>
                <!-- eslint-enable -->
            </transition>
        </div>
    </div>
</template>

<script setup>
import BaseIcon from './BaseIcon.vue';

const accordion = ref(null);

defineProps({
    items: {
        type: Array,
        required: true,
        default() { return []; }
    }
});

const openIndexes = ref([]);

const toggle = (index) => {
    if (openIndexes.value.includes(index)) {
        openIndexes.value = openIndexes.value.filter((i) => i !== index);
    } else {
        openIndexes.value.push(index);
    }
};

const enter = (element) => {
    const width = getComputedStyle(element).width;

    element.style.width = width;
    element.style.position = 'absolute';
    element.style.visibility = 'hidden';
    element.style.height = 'auto';

    const height = getComputedStyle(element).height;

    element.style.width = null;
    element.style.position = null;
    element.style.visibility = null;
    element.style.height = 0;

    // Force repaint to make sure the
    // animation is triggered correctly.
    getComputedStyle(element).height;

    // Trigger the animation.
    // We use `requestAnimationFrame` because we need
    // to make sure the browser has finished
    // painting after setting the `height`
    // to `0` in the line above.
    requestAnimationFrame(() => {
        element.style.height = height;
    });
};

const afterEnter = (element) => {
    element.style.height = 'auto';
};

const leave = (element) => {
    const height = getComputedStyle(element).height;

    element.style.height = height;

    // Force repaint to make sure the
    // animation is triggered correctly.
    getComputedStyle(element).height;

    requestAnimationFrame(() => {
        element.style.height = 0;
    });
};
</script>

<style lang="less">
.base-accordion__item {
    border-bottom: 1px solid var(--color-darkest);
    margin-bottom: .5rem;
}

.base-accordion__button {
    text-align: left;
    appearance: none;
    -webkit-appearance: none;

    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 0;
    background: transparent;
    color: inherit;
    width: 100%;
    padding: var(--spacing-xs) 0;
    line-height: 1;
    cursor: pointer;
    transition: color var(--transition-timing-medium) var(--transition-timingFunction-in-out);
    font-size: var(--font-size-md);

    @media @q-md-min {
        padding: var(--spacing-xs) 0;
        font-size: var(--font-size-lg);
    }

    &:hover {
        color: var(--color-tertiary-light);
    }

    &:active {
        color: var(--color-darkest);
    }
}

.base-accordion__icon {
    font-size: 1.5rem;
    width: 1.5rem;
    height: 1.5rem;
}

.base-accordion__content {
    .base-accordion__content-html {
        padding-bottom: var(--spacing-md);
    }

    &, & p {
        .typography-text-sm();

        @media @q-md-min {
            font-size: var(--font-size-lg);
        }
    }

    a {
        text-decoration: none;
    }
}

.expand-enter-active,
.expand-leave-active {
    transition: height var(--transition-timing-medium) var(--transition-timingFunction-in-out), opacity var(--transition-timing-medium) var(--transition-timingFunction-in-out);
    overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
    height: 0;
    opacity: 0;
}
</style>
